/** Path C additive renderers for the compliance tab.
 *
 * Each component is null-safe: returns null when its source field is
 * absent from the output. So when the platform-side compliance prompt
 * hasn't been updated, the compliance tab looks identical to the
 * pre-Path-C version.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Gavel, ShieldAlert, ShieldX } from 'lucide-react';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const RULE_VERDICT_CLS: Record<string, string> = {
  pass: 'bg-green-50 text-green-700 border-green-200',
  fail: 'bg-red-50 text-red-800 border-red-300',
  n_a: 'bg-muted text-muted-foreground border-border',
};

export function PathCRuleEvaluations({ output }: { output: Record<string, unknown> }) {
  const evals = asArray(output.gate_rule_evaluations);
  if (evals.length === 0) return null;
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4 space-y-1.5">
        <div className="flex items-center gap-2">
          <Gavel className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Tenant gate-rule evaluations ({evals.length})</span>
        </div>
        <ul className="space-y-1 text-xs">
          {evals.map((e, i) => {
            const o = obj(e);
            const verdict = (asText(o.verdict) || 'n_a').toLowerCase();
            const cls = RULE_VERDICT_CLS[verdict] ?? '';
            const clauseIds = asArray(o.evidence_clause_ids).map((x) => asText(x)).filter(Boolean);
            const kbIds = asArray(o.kb_citation_ids).map((x) => asText(x)).filter(Boolean);
            return (
              <li key={i} className="rounded border px-2 py-1.5 space-y-0.5">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className={`text-[10px] shrink-0 uppercase ${cls}`}>
                    {verdict}
                  </Badge>
                  <span className="font-medium">{asText(o.rule_name) || '—'}</span>
                </div>
                {asText(o.reasoning) && <p>{asText(o.reasoning)}</p>}
                {(clauseIds.length > 0 || kbIds.length > 0) && (
                  <p className="text-[10px] text-muted-foreground">
                    {clauseIds.length > 0 && <>evidence: {clauseIds.join(', ')}</>}
                    {clauseIds.length > 0 && kbIds.length > 0 && <> · </>}
                    {kbIds.length > 0 && <>kb: {kbIds.join(', ')}</>}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export function PathCKbCitations({ output }: { output: Record<string, unknown> }) {
  const citations = asArray(output.kb_citations);
  if (citations.length === 0) return null;
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4 space-y-1">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">KB citations ({citations.length})</span>
        </div>
        <ul className="space-y-1 text-xs">
          {citations.map((c, i) => {
            const o = obj(c);
            return (
              <li key={i} className="rounded border px-2 py-1 space-y-0.5">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                    {asText(o.id) || '—'}
                  </span>
                  <span className="font-medium">{asText(o.title) || '—'}</span>
                </div>
                {asText(o.snippet) && (
                  <p className="text-muted-foreground italic">{asText(o.snippet)}</p>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function FailureRows({ items, tone }: { items: unknown[]; tone: 'red' | 'orange' }) {
  const textCls =
    tone === 'red'
      ? 'text-red-700 dark:text-red-200'
      : 'text-orange-700 dark:text-orange-200';
  return (
    <ul className={`space-y-0.5 text-xs ${textCls}`}>
      {items.map((f, i) => {
        const o = obj(f);
        return (
          <li key={i} className="flex items-start gap-2">
            <span className="font-medium">{asText(o.rule_name) || '—'}</span>
            {asText(o.related_clause_id) && (
              <span className="text-muted-foreground">[{asText(o.related_clause_id)}]</span>
            )}
            {asText(o.description) && <span>· {asText(o.description)}</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function PathCFailures({ output }: { output: Record<string, unknown> }) {
  const blocking = asArray(output.blocking_failures);
  const major = asArray(output.major_failures);
  if (blocking.length === 0 && major.length === 0) return null;
  return (
    <div className="space-y-2">
      {blocking.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm dark:border-red-700 dark:bg-red-950/30">
          <p className="font-medium text-red-800 dark:text-red-300 flex items-center gap-1 mb-1">
            <ShieldX className="h-3.5 w-3.5" aria-hidden="true" />
            Blocking failures ({blocking.length}) — drives deterministic REJECT
          </p>
          <FailureRows items={blocking} tone="red" />
        </div>
      )}
      {major.length > 0 && (
        <div className="rounded border border-orange-300 bg-orange-50 p-3 text-sm dark:border-orange-700 dark:bg-orange-950/30">
          <p className="font-medium text-orange-800 dark:text-orange-300 flex items-center gap-1 mb-1">
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
            Major failures ({major.length}) — drives deterministic REVIEW
          </p>
          <FailureRows items={major} tone="orange" />
        </div>
      )}
    </div>
  );
}
